import { useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { T, useGym } from '../../lib/gym';
import { useI18n } from '../../lib/i18n';
import { navigate } from '../../lib/router';
import { AsyncButton, Button, Card, Checkbox, cx, ErrorBox, Field, Input, Loading, Select, useToast } from '../../ui/components';

type Step = 'basics' | 'kind' | 'lessons' | 'prices' | 'team' | 'members' | 'payments' | 'website';
interface Lesson { id?: string; name: string; durationMin: number; capacity: number; trialEligible: boolean }
interface Slot { lesson: number; weekdays: number[]; time: string }
type Span = [string, string] | null;

const toBase64 = (f: File): Promise<string> => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(',')[1]);
  r.onerror = () => reject(r.error);
  r.readAsDataURL(f);
});
const euros = (cents: number): string => (cents / 100).toFixed(2).replace(/\.00$/, '');
const cents = (v: string): number => Math.round(Number(String(v).replace(',', '.')) * 100);
const WEEK = [1, 2, 3, 4, 5, 6, 0];

/**
 * The first hour of a new customer: eight steps that turn the starter kit into their own gym.
 * Beside the form, a preview shows what members will see, so the owner watches the gym take shape.
 */
export function SetupFlowPage() {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const base = `${T(gym.slug)}/admin/welcome`;
  const [view, setView] = useState<any | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [step, setStep] = useState<Step | 'done'>('basics');

  const load = async (): Promise<void> => {
    try {
      const v = await api<any>(base);
      setView(v);
      if (v.state) {
        const handled = v.steps.every((s: Step) => v.state.done.includes(s) || v.state.skipped.includes(s));
        setStep(v.state.finishedAt || handled ? 'done' : v.state.step);
      }
    } catch (e) { setError(e); }
  };
  useEffect(() => { void load(); }, []);

  const after = async (r: any, message?: string): Promise<void> => {
    if (message) toast.ok(message);
    const v = await api<any>(base);
    setView(v);
    const handled = v.steps.every((s: Step) => v.state.done.includes(s) || v.state.skipped.includes(s));
    setStep(handled ? 'done' : (r?.state?.step ?? v.state.step));
    window.scrollTo({ top: 0 });
  };
  const skip = async (s: Step): Promise<void> => after(await api(`${base}/steps/${s}/skip`, { body: {} }));

  if (error) return <div className="page"><ErrorBox error={error} onRetry={load} /></div>;
  if (!view) return <Loading />;
  if (!view.active) {
    return <div className="page"><Card><p>{i18n.t('error.onboarding_not_active')}</p><Button onClick={() => navigate(`${gym.base}/admin`)}>{i18n.t('onboard.go_dashboard')}</Button></Card></div>;
  }
  const steps: Step[] = view.steps;
  const index = step === 'done' ? steps.length : steps.indexOf(step);

  return (
    <div className="page onb">
      <header className="onb-head">
        <h1>{i18n.t('onboard.title', { gym: view.gym.name })}</h1>
        <p className="muted">{i18n.t('onboard.subtitle')}</p>
      </header>
      <ol className="onb-rail" aria-label={i18n.t('onboard.step_of', { n: Math.min(index + 1, steps.length), total: steps.length })}>
        {steps.map((s, i) => {
          const state = view.state.done.includes(s) ? 'done' : view.state.skipped.includes(s) ? 'skipped' : '';
          return (
            <li key={s} className={cx(state, s === step && 'current')}>
              <button type="button" onClick={() => setStep(s)} aria-current={s === step ? 'step' : undefined}>
                <span className="onb-num" aria-hidden="true">{state === 'done' ? '✓' : i + 1}</span>
                <span>{i18n.t(`onboard.step_${s}`)}</span>
              </button>
            </li>
          );
        })}
      </ol>

      {step === 'done' ? <Done view={view} base={base} /> : (
        <div className={cx('onb-body', (step === 'basics' || step === 'lessons') && 'with-preview')}>
          <div className="onb-form">
            <p className="small muted">{i18n.t('onboard.step_of', { n: index + 1, total: steps.length })}</p>
            {step === 'basics' && <Basics view={view} base={base} onSaved={after} />}
            {step === 'kind' && <Kind view={view} base={base} onSaved={after} />}
            {step === 'lessons' && <Lessons view={view} base={base} onSaved={after} onSkip={() => skip('lessons')} />}
            {step === 'prices' && <Prices view={view} base={base} onSaved={after} onSkip={() => skip('prices')} />}
            {step === 'team' && <Team base={base} onSaved={after} onSkip={() => skip('team')} />}
            {step === 'members' && <Members view={view} base={base} onSaved={after} onSkip={() => skip('members')} />}
            {step === 'payments' && <Payments view={view} base={base} onSaved={after} onSkip={() => skip('payments')} />}
            {step === 'website' && <Website view={view} base={base} onSaved={after} onSkip={() => skip('website')} />}
            {index > 0 && <Button variant="ghost" onClick={() => setStep(steps[index - 1])}>{i18n.t('onboard.back')}</Button>}
          </div>
        </div>
      )}
    </div>
  );
}

interface StepProps { view?: any; base: string; onSaved(r: any, message?: string): Promise<void>; onSkip?(): Promise<void> }

function Actions({ onContinue, onSkip, disabled }: { onContinue(): Promise<void>; onSkip?: () => Promise<void>; disabled?: boolean }) {
  const i18n = useI18n();
  return (
    <div className="onb-actions">
      {onSkip && <AsyncButton variant="secondary" onClick={onSkip}>{i18n.t('onboard.skip')}</AsyncButton>}
      <AsyncButton disabled={disabled} onClick={onContinue}>{i18n.t('onboard.continue')}</AsyncButton>
    </div>
  );
}

// ---------- Step 1: the gym itself ----------

function Basics({ view, base, onSaved }: StepProps) {
  const gym = useGym();
  const i18n = useI18n();
  const [v, setV] = useState({ ...view.gym });
  const set = (k: string, value: unknown): void => setV((x: any) => ({ ...x, [k]: value }));
  const setSpan = (day: 'weekdays' | 'saturday' | 'sunday', span: Span): void => setV((x: any) => ({ ...x, hours: { ...x.hours, [day]: span } }));
  return (
    <>
      <div className="onb-split">
        <section>
          <h2>{i18n.t('onboard.basics_title')}</h2>
          <p className="muted">{i18n.t('onboard.basics_intro')}</p>
          <Field label={i18n.t('onboard.name')}><Input value={v.name} onChange={(e) => set('name', e.target.value)} /></Field>
          <Field label={i18n.t('onboard.email')} hint={i18n.t('onboard.email_hint')}><Input type="email" value={v.email} onChange={(e) => set('email', e.target.value)} /></Field>
          <Field label={i18n.t('onboard.street')}><Input value={v.street} onChange={(e) => set('street', e.target.value)} /></Field>
          <div className="grid-2">
            <Field label={i18n.t('onboard.postal_code')}><Input value={v.postalCode} onChange={(e) => set('postalCode', e.target.value)} /></Field>
            <Field label={i18n.t('onboard.city')}><Input value={v.city} onChange={(e) => set('city', e.target.value)} /></Field>
          </div>
          <div className="grid-2">
            <Field label={i18n.t('onboard.logo')} hint={i18n.t('onboard.logo_hint')}>
              <input type="file" accept="image/png,image/jpeg,image/webp" aria-label={i18n.t('onboard.upload_logo')} onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                const r = await api(`${T(gym.slug)}/admin/uploads`, { body: { contentType: f.type, dataBase64: await toBase64(f), visibility: 'public', filename: f.name } });
                set('logoUrl', r.url);
              }} />
            </Field>
            <Field label={i18n.t('onboard.colour')} hint={i18n.t('onboard.colour_hint')}>
              <input type="color" className="onb-colour" value={v.primaryColor} aria-label={i18n.t('onboard.colour')} onChange={(e) => set('primaryColor', e.target.value)} />
            </Field>
          </div>
          <fieldset className="onb-hours">
            <legend>{i18n.t('onboard.hours')}</legend>
            {(['weekdays', 'saturday', 'sunday'] as const).map((day) => (
              <HoursRow key={day} label={i18n.t(`onboard.${day}`)} span={v.hours?.[day] ?? null} onChange={(span) => setSpan(day, span)} />
            ))}
          </fieldset>
        </section>
        <PreviewApp gym={v} />
      </div>
      <Actions disabled={!String(v.name ?? '').trim()} onContinue={async () => onSaved(await api(`${base}/basics`, { body: v }))} />
    </>
  );
}

function HoursRow({ label, span, onChange }: { label: string; span: Span; onChange(s: Span): void }) {
  const i18n = useI18n();
  const closed = !span;
  return (
    <div className="onb-hours-row">
      <span>{label}</span>
      <Checkbox label={i18n.t('onboard.closed')} checked={closed} onChange={(c) => onChange(c ? null : ['09:00', '17:00'])} />
      {!closed && (
        <>
          <Input type="time" aria-label={`${label} ${i18n.t('onboard.from')}`} value={span![0]} onChange={(e) => onChange([e.target.value, span![1]])} />
          <Input type="time" aria-label={`${label} ${i18n.t('onboard.to')}`} value={span![1]} onChange={(e) => onChange([span![0], e.target.value])} />
        </>
      )}
    </div>
  );
}

/** What members see in the app: the gym's own name, logo, colour and hours. */
function PreviewApp({ gym }: { gym: any }) {
  const i18n = useI18n();
  const colour = /^#[0-9a-f]{6}$/i.test(gym.primaryColor ?? '') ? gym.primaryColor : '#4f46e5';
  const line = (span: Span): string => (span ? `${span[0]} – ${span[1]}` : i18n.t('onboard.closed'));
  return (
    <aside className="onb-preview" aria-label={i18n.t('onboard.preview')}>
      <p className="small muted">{i18n.t('onboard.preview')}</p>
      <div className="onb-phone" style={{ ['--onb-brand' as string]: colour }}>
        <div className="onb-phone-bar">{gym.logoUrl ? <img src={gym.logoUrl} alt="" /> : <strong>{gym.name || '…'}</strong>}</div>
        <div className="onb-phone-body">
          <p className="onb-phone-name">{gym.name || '…'}</p>
          <p className="small muted">{[gym.street, gym.city].filter(Boolean).join(', ')}</p>
          <dl className="onb-phone-hours">
            <dt>{i18n.t('onboard.weekdays')}</dt><dd>{line(gym.hours?.weekdays ?? null)}</dd>
            <dt>{i18n.t('onboard.saturday')}</dt><dd>{line(gym.hours?.saturday ?? null)}</dd>
            <dt>{i18n.t('onboard.sunday')}</dt><dd>{line(gym.hours?.sunday ?? null)}</dd>
          </dl>
          <span className="onb-phone-button">{i18n.t('onboard.link_trial')}</span>
        </div>
      </div>
    </aside>
  );
}

// ---------- Step 2: what kind of gym ----------

function Kind({ view, base, onSaved }: StepProps) {
  const i18n = useI18n();
  return (
    <section>
      <h2>{i18n.t('onboard.kind_title')}</h2>
      <p className="muted">{i18n.t('onboard.kind_intro')}</p>
      <div className="onb-kinds">
        {view.kinds.map((k: string) => (
          <AsyncButton key={k} variant={view.state.kind === k ? 'primary' : 'secondary'} className="onb-kind" onClick={async () => onSaved(await api(`${base}/kind`, { body: { kind: k } }))}>
            <strong>{i18n.t(`onboard.kind_${k}`)}</strong>
            <span>{i18n.t(`onboard.kind_${k}_text`)}</span>
          </AsyncButton>
        ))}
      </div>
    </section>
  );
}

// ---------- Step 3: lessons and the weekly timetable ----------

function Lessons({ view, base, onSaved, onSkip }: StepProps) {
  const i18n = useI18n();
  const initial = useMemo(() => {
    // Coming back to this step shows the timetable made before; the first time, the suggestions for this kind of gym.
    if (view.slots.length) {
      const lessons: Lesson[] = view.lessons.map((l: any) => ({ id: l.id, name: l.name, durationMin: l.durationMin, capacity: l.capacity, trialEligible: l.trialEligible }));
      const slots: Slot[] = view.slots.map((s: any) => ({ lesson: Math.max(0, lessons.findIndex((l) => l.id === s.lessonId)), weekdays: s.weekdays, time: s.time }));
      return { lessons, slots };
    }
    const preset = view.preset;
    if (!preset) return { lessons: view.lessons.map((l: any) => ({ ...l })), slots: [] as Slot[] };
    const lessons: Lesson[] = preset.lessons.map((p: any, i: number) => ({ ...p, id: view.lessons[i]?.id }));
    return { lessons, slots: preset.slots.map((s: any) => ({ ...s })) };
  }, [view]);
  const [lessons, setLessons] = useState<Lesson[]>(initial.lessons);
  const [slots, setSlots] = useState<Slot[]>(initial.slots);
  const editLesson = (i: number, patch: Partial<Lesson>): void => setLessons((xs) => xs.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const editSlot = (i: number, patch: Partial<Slot>): void => setSlots((xs) => xs.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const removeLesson = (i: number): void => {
    setLessons((xs) => xs.filter((_, j) => j !== i));
    setSlots((xs) => xs.filter((s) => s.lesson !== i).map((s) => ({ ...s, lesson: s.lesson > i ? s.lesson - 1 : s.lesson })));
  };
  return (
    <>
      <div className="onb-split">
        <section>
          <h2>{i18n.t('onboard.lessons_title')}</h2>
          <p className="muted">{i18n.t('onboard.lessons_intro')}</p>
          {lessons.map((l, i) => (
            <div key={i} className="onb-lesson">
              <Field label={i18n.t('onboard.lesson_name')}><Input value={l.name} onChange={(e) => editLesson(i, { name: e.target.value })} /></Field>
              <Field label={i18n.t('onboard.duration')}><Input type="number" min={15} max={240} value={l.durationMin} onChange={(e) => editLesson(i, { durationMin: Number(e.target.value) })} /></Field>
              <Field label={i18n.t('onboard.capacity')}><Input type="number" min={1} max={200} value={l.capacity} onChange={(e) => editLesson(i, { capacity: Number(e.target.value) })} /></Field>
              <Checkbox label={i18n.t('onboard.trial')} checked={l.trialEligible} onChange={(c) => editLesson(i, { trialEligible: c })} />
              {lessons.length > 1 && <Button size="sm" variant="ghost" onClick={() => removeLesson(i)}>{i18n.t('onboard.remove')}</Button>}
            </div>
          ))}
          <p className="small muted">{i18n.t('onboard.capacity_hint')}</p>
          <Button size="sm" variant="secondary" onClick={() => setLessons((xs) => [...xs, { name: '', durationMin: 60, capacity: 10, trialEligible: true }])}>+ {i18n.t('onboard.add_lesson')}</Button>

          <h3>{i18n.t('onboard.timetable')}</h3>
          <p className="small muted">{i18n.t('onboard.timetable_intro')}</p>
          {slots.map((s, i) => (
            <div key={i} className="onb-slot">
              <Select aria-label={i18n.t('onboard.lesson')} value={String(s.lesson)} onChange={(e) => editSlot(i, { lesson: Number(e.target.value) })}
                options={lessons.map((l, j) => ({ value: String(j), label: l.name || '…' }))} />
              <div className="onb-days" role="group" aria-label={i18n.t('onboard.days')}>
                {WEEK.map((d) => (
                  <button key={d} type="button" aria-pressed={s.weekdays.includes(d)}
                    onClick={() => editSlot(i, { weekdays: s.weekdays.includes(d) ? s.weekdays.filter((x) => x !== d) : [...s.weekdays, d] })}>
                    {i18n.t(`onboard.day_${d}`)}
                  </button>
                ))}
              </div>
              <Input type="time" aria-label={i18n.t('onboard.time')} value={s.time} onChange={(e) => editSlot(i, { time: e.target.value })} />
              <Button size="sm" variant="ghost" onClick={() => setSlots((xs) => xs.filter((_, j) => j !== i))}>{i18n.t('onboard.remove')}</Button>
            </div>
          ))}
          <Button size="sm" variant="secondary" onClick={() => setSlots((xs) => [...xs, { lesson: 0, weekdays: [1], time: '19:00' }])}>+ {i18n.t('onboard.add_slot')}</Button>
        </section>
        <PreviewWeek lessons={lessons} slots={slots} />
      </div>
      <Actions onSkip={onSkip} disabled={!lessons.some((l) => l.name.trim())} onContinue={async () => {
        const r = await api(`${base}/lessons`, { body: { lessons, slots } });
        await onSaved(r, i18n.t('onboard.sessions_planned', { count: r.sessionsPlanned }));
      }} />
    </>
  );
}

/** Their own week, as members will see it in the timetable. */
function PreviewWeek({ lessons, slots }: { lessons: Lesson[]; slots: Slot[] }) {
  const i18n = useI18n();
  const byDay = WEEK.map((d) => slots.filter((s) => s.weekdays.includes(d)).map((s) => ({ ...s, name: lessons[s.lesson]?.name ?? '' })).sort((a, b) => a.time.localeCompare(b.time)));
  return (
    <aside className="onb-preview" aria-label={i18n.t('onboard.preview')}>
      <p className="small muted">{i18n.t('onboard.preview')}</p>
      <div className="onb-week">
        {WEEK.map((d, i) => (
          <div key={d} className="onb-day">
            <span className="onb-day-name">{i18n.t(`onboard.day_${d}`)}</span>
            {byDay[i].map((s, j) => <span key={j} className="onb-block"><b>{s.time}</b> {s.name || '…'}</span>)}
          </div>
        ))}
      </div>
    </aside>
  );
}

// ---------- Step 4: prices ----------

function Prices({ view, base, onSaved, onSkip }: StepProps) {
  const i18n = useI18n();
  const [plans, setPlans] = useState(view.plans.map((p: any) => ({ ...p, price: euros(p.priceCents) })));
  const [pack, setPack] = useState(view.pack ? { ...view.pack, price: euros(view.pack.priceCents) } : null);
  const edit = (i: number, patch: Record<string, unknown>): void => setPlans((xs: any[]) => xs.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  return (
    <section>
      <h2>{i18n.t('onboard.prices_title')}</h2>
      <p className="muted">{i18n.t('onboard.prices_intro')}</p>
      {plans.map((p: any, i: number) => (
        <div key={p.id} className="onb-price">
          <Field label={i18n.t('onboard.plan_name')}><Input value={p.name} onChange={(e) => edit(i, { name: e.target.value })} /></Field>
          <Field label={i18n.t('onboard.price')}><Input inputMode="decimal" value={p.price} onChange={(e) => edit(i, { price: e.target.value })} /></Field>
          <Field label={i18n.t('onboard.per_week')}><Input type="number" min={1} max={14} value={p.perWeek} onChange={(e) => edit(i, { perWeek: Number(e.target.value) })} /></Field>
          <Checkbox label={i18n.t('onboard.active')} checked={p.active} onChange={(c) => edit(i, { active: c })} />
        </div>
      ))}
      {pack && (
        <div className="onb-price">
          <Field label={i18n.t('onboard.pack')}><Input value={pack.name} onChange={(e) => setPack({ ...pack, name: e.target.value })} /></Field>
          <Field label={i18n.t('onboard.pack_price')}><Input inputMode="decimal" value={pack.price} onChange={(e) => setPack({ ...pack, price: e.target.value })} /></Field>
          <Field label={i18n.t('onboard.pack_lessons')}><Input type="number" min={1} max={100} value={pack.quantity} onChange={(e) => setPack({ ...pack, quantity: Number(e.target.value) })} /></Field>
        </div>
      )}
      <Actions onSkip={onSkip} onContinue={async () => onSaved(await api(`${base}/prices`, {
        body: {
          plans: plans.map((p: any) => ({ id: p.id, name: p.name, priceCents: cents(p.price), perWeek: p.perWeek, active: p.active })),
          pack: pack ? { id: pack.id, name: pack.name, priceCents: cents(pack.price), quantity: pack.quantity } : undefined,
        },
      }))} />
    </section>
  );
}

// ---------- Step 5: the team ----------

function Team({ base, onSaved, onSkip }: StepProps) {
  const i18n = useI18n();
  const blank = { firstName: '', lastName: '', email: '', role: 'trainer', employment: 'freelancer' };
  const [rows, setRows] = useState([{ ...blank }]);
  const [results, setResults] = useState<any[] | null>(null);
  const edit = (i: number, patch: Record<string, string>): void => setRows((xs) => xs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <section>
      <h2>{i18n.t('onboard.team_title')}</h2>
      <p className="muted">{i18n.t('onboard.team_intro')}</p>
      {rows.map((r, i) => (
        <div key={i} className="onb-person">
          <Field label={i18n.t('onboard.first_name')}><Input value={r.firstName} onChange={(e) => edit(i, { firstName: e.target.value })} /></Field>
          <Field label={i18n.t('onboard.last_name')}><Input value={r.lastName} onChange={(e) => edit(i, { lastName: e.target.value })} /></Field>
          <Field label={i18n.t('onboard.email')}><Input type="email" value={r.email} onChange={(e) => edit(i, { email: e.target.value })} /></Field>
          <Field label={i18n.t('onboard.role')}>
            <Select value={r.role} onChange={(e) => edit(i, { role: e.target.value })}
              options={['trainer', 'desk', 'manager'].map((x) => ({ value: x, label: i18n.t(`onboard.role_${x}`) }))} />
          </Field>
          {r.role === 'trainer' && (
            <Field label={i18n.t('onboard.employment')}>
              <Select value={r.employment} onChange={(e) => edit(i, { employment: e.target.value })}
                options={['freelancer', 'employee_permanent'].map((x) => ({ value: x, label: i18n.t(`onboard.employment_${x}`) }))} />
            </Field>
          )}
          {results?.[i] && <p className={cx('small', results[i].status === 'invalid' ? 'warn' : 'muted')}>{i18n.t(`onboard.status_${results[i].status}`)}</p>}
        </div>
      ))}
      <Button size="sm" variant="secondary" onClick={() => setRows((xs) => [...xs, { ...blank }])}>+ {i18n.t('onboard.add_person')}</Button>
      <Actions onSkip={onSkip} disabled={!rows.some((r) => r.email.trim())} onContinue={async () => {
        const r = await api(`${base}/team`, { body: { people: rows.filter((x) => x.email.trim()) } });
        setResults(r.results);
        if (r.results.some((x: any) => x.status === 'invalid')) return;
        await onSaved(r, i18n.t('onboard.invited', { count: r.invited }));
      }} />
    </section>
  );
}

// ---------- Steps 6 and 7: members and payments live on their own screens ----------

function Members({ base, onSkip }: StepProps) {
  const gym = useGym();
  const i18n = useI18n();
  return (
    <section>
      <h2>{i18n.t('onboard.members_title')}</h2>
      <p className="muted">{i18n.t('onboard.members_intro')}</p>
      <div className="onb-choices">
        <Card>
          <h3>{i18n.t('onboard.members_import')}</h3>
          <p className="small muted">{i18n.t('onboard.members_import_text')}</p>
          <AsyncButton onClick={async () => { await api(`${base}/steps/members/done`, { body: {} }); navigate(`${gym.base}/admin/import`); }}>{i18n.t('onboard.members_import')}</AsyncButton>
        </Card>
        <Card>
          <h3>{i18n.t('onboard.members_later')}</h3>
          <p className="small muted">{i18n.t('onboard.members_later_text')}</p>
          <AsyncButton variant="secondary" onClick={onSkip!}>{i18n.t('onboard.members_later')}</AsyncButton>
        </Card>
      </div>
    </section>
  );
}

function Payments({ view, base, onSaved, onSkip }: StepProps) {
  const gym = useGym();
  const i18n = useI18n();
  return (
    <section>
      <h2>{i18n.t('onboard.payments_title')}</h2>
      <p className="muted">{i18n.t('onboard.payments_intro')}</p>
      <Card className={view.payments.connected ? '' : 'risk risk-medium'}>
        <p>{i18n.t(view.payments.connected ? 'onboard.payments_connected' : 'onboard.payments_not_connected')}</p>
        {gym.gym.testMode && <p className="small muted">{i18n.t('onboard.payments_test')}</p>}
        {!view.payments.connected && <Button variant="secondary" onClick={() => navigate(`${gym.base}/admin/integrations`)}>{i18n.t('onboard.payments_connect')}</Button>}
      </Card>
      <Actions onSkip={view.payments.connected ? undefined : onSkip} onContinue={async () => onSaved(await api(`${base}/steps/payments/done`, { body: {} }))} />
    </section>
  );
}

// ---------- Step 8: the website ----------

function Website({ view, base, onSaved, onSkip }: StepProps) {
  const i18n = useI18n();
  const suggested = view.state.kind === 'box' ? 'box' : view.state.kind === 'pt' ? 'coach' : 'studio';
  return (
    <section>
      <h2>{i18n.t('onboard.website_title')}</h2>
      {view.websiteIncluded ? (
        <>
          <p className="muted">{i18n.t('onboard.website_intro')}</p>
          <div className="onb-kinds">
            {['studio', 'box', 'coach'].map((theme) => (
              <AsyncButton key={theme} variant={theme === suggested ? 'primary' : 'secondary'} className="onb-kind"
                onClick={async () => onSaved(await api(`${base}/website`, { body: { theme } }))}>
                <strong>{i18n.t(`siteedit.theme_${theme}`)}</strong>
                <span>{i18n.t(`siteedit.theme_${theme}_text`)}</span>
              </AsyncButton>
            ))}
          </div>
          <div className="onb-actions"><AsyncButton variant="secondary" onClick={onSkip!}>{i18n.t('onboard.skip')}</AsyncButton></div>
        </>
      ) : (
        <>
          <p className="muted">{i18n.t('onboard.website_not_included')}</p>
          <Actions onContinue={async () => onSaved(await api(`${base}/website`, { body: {} }))} />
        </>
      )}
    </section>
  );
}

// ---------- Finished ----------

function Done({ view, base }: { view: any; base: string }) {
  const gym = useGym();
  const i18n = useI18n();
  const [copied, setCopied] = useState('');
  const open = view.checklist.steps.filter((s: any) => !s.done);
  return (
    <section className="onb-done">
      <h2>{i18n.t('onboard.done_title')}</h2>
      <p className="muted">{i18n.t('onboard.done_intro')}</p>
      <dl className="onb-links">
        {(['schedule', 'trial', 'join'] as const).map((k) => (
          <div key={k}>
            <dt>{i18n.t(`onboard.link_${k}`)}</dt>
            <dd>
              <a href={view.links[k]} target="_blank" rel="noreferrer">{view.links[k]}</a>
              <Button size="sm" variant="ghost" onClick={async () => { await navigator.clipboard?.writeText(view.links[k]); setCopied(k); }}>
                {i18n.t(copied === k ? 'onboard.copied' : 'onboard.copy')}
              </Button>
            </dd>
          </div>
        ))}
      </dl>
      {open.length > 0 && (
        <>
          <h3>{i18n.t('onboard.done_next')}</h3>
          <ul className="sets">{open.map((s: any) => <li key={s.key}><a href={s.link}>{i18n.t(`setup.step_${s.key}`)}</a></li>)}</ul>
        </>
      )}
      <AsyncButton onClick={async () => { await api(`${base}/finish`, { body: {} }); navigate(`${gym.base}/admin`); }}>{i18n.t('onboard.go_dashboard')}</AsyncButton>
    </section>
  );
}
